/**
 * IndustryWACCComparisonChart
 *
 * Horizontal bar chart showing WACC (%) per company for a selected year.
 * Mirrors the FID WACCTrendChart at industry level with per-company comparison.
 * Uses YearNavigator for year selection, matching IndustryROICTrendChart style.
 */
import { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import { formatFiscalYear } from '../../financial/utils/formatters';
import YearNavigator from './YearNavigator';
import type { RegionConfig } from '../../../types/financial';
import type { CompanyTimeSeries, IndustryBenchmarkRecord } from '../../../types/industryResearch';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16'];
const MEDIAN_COLOR = '#6366f1';

interface Props {
  companies: CompanyTimeSeries[];
  medianData: IndustryBenchmarkRecord[];
  region?: RegionConfig;
}

function toPct(val: number | null | undefined): number | null {
  if (val == null) return null;
  return parseFloat((Math.abs(val) <= 1 ? val * 100 : val).toFixed(2));
}

export default function IndustryWACCComparisonChart({ companies, medianData, region }: Props) {
  const years = medianData.map((d) => d.year).sort((a, b) => a - b);
  const [selectedYear, setSelectedYear] = useState(years[years.length - 1] ?? 0);

  const barData = [
    ...companies.map((co, i) => {
      const rec = co.data.find((d) => d.year === selectedYear);
      return {
        name: co.company_name.length > 22 ? co.company_name.slice(0, 20) + '...' : co.company_name,
        fullName: co.company_name,
        value: toPct(rec?.wacc),
        color: COLORS[i % COLORS.length],
      };
    }).sort((a, b) => (a.value ?? 0) - (b.value ?? 0)), // lower WACC first (better)
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between w-full">
          <div>
            <h3 className="text-sm font-semibold text-neutral-800">WACC by Company (%)</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Weighted Average Cost of Capital — lower is better</p>
          </div>
          <YearNavigator
            years={years}
            selectedYear={selectedYear}
            onChange={setSelectedYear}
            formatYear={(y) => formatFiscalYear(y, region) ?? `FY${y}`}
          />
        </div>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={Math.max(200, barData.length * 48 + 30)}>
          <BarChart data={barData} layout="vertical" margin={{ top: 4, right: 50, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} />
            <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(v: number) => [`${v?.toFixed(2)}%`, 'WACC']}
              labelFormatter={(l) => barData.find(d => d.name === l)?.fullName ?? l}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={26}
              label={{ position: 'right', fontSize: 10, fill: '#6b7280', formatter: (v: number) => v != null ? `${v}%` : '' }}>
              {barData.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  );
}
